// Standards Library - browse every control/clause across all standards
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  HStack,
  VStack,
  Stack,
  SimpleGrid,
  Grid,
  GridItem,
  Card,
  Badge,
  Button,
  Input,
  Spinner,
  Icon,
  Collapsible,
  IconButton,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiLayers,
  FiBookOpen,
  FiList,
  FiArrowLeft,
  FiX,
} from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useSystemStandards } from '../hooks/useApi';
import { systemStandardsAPI } from '../services/api';

// ----- Helpers (single mapping, used across the page) -----
const getStandardIcon = (standardType: string): string => {
  const t = (standardType || '').toUpperCase();
  if (t.includes('ISO')) return '🔒';
  if (t.includes('SOC')) return '🏢';
  if (t.includes('NIST')) return '🛡️';
  if (t.includes('GDPR')) return '📋';
  if (t.includes('PCI')) return '💳';
  if (t.includes('DGA')) return '🏛️';
  return '📄';
};

const getStandardColor = (standardType: string): string => {
  const t = (standardType || '').toUpperCase();
  if (t.includes('ISO')) return 'blue';
  if (t.includes('SOC')) return 'green';
  if (t.includes('NIST')) return 'purple';
  if (t.includes('GDPR')) return 'orange';
  if (t.includes('PCI')) return 'teal';
  if (t.includes('DGA')) return 'cyan';
  return 'gray';
};

// Presentation colour for the admin-set classification (Standard/Framework/Regulation)
const getClassificationColor = (classification?: string): string => {
  switch ((classification || '').toLowerCase()) {
    case 'standard':
      return 'blue';
    case 'framework':
      return 'purple';
    case 'regulation':
      return 'orange';
    default:
      return 'gray';
  }
};

// Remove a leading "<id>:" / "<id>)" prefix so the clause text reads cleanly
const stripIdPrefix = (content: string, id: string): string => {
  const c = (content || '').trimStart();
  if (c.startsWith(`${id}:`)) return c.slice(id.length + 1).trimStart();
  if (c.startsWith(`${id})`)) return c.slice(id.length + 1).trimStart();
  if (c.startsWith(`${id} `)) return c.slice(id.length).trimStart();
  return content;
};

const sectionMatches = (section: any, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  const has = (s?: string) => (s || '').toLowerCase().includes(needle);
  if (has(section.section_title) || has(section.section_content) || has(section.section_id)) return true;
  for (const sub of section.subsections || []) {
    if (has(sub.subsection_title) || has(sub.subsection_content) || has(sub.subsection_id)) return true;
    for (const ss of sub.sub_subsections || []) {
      if (has(ss.title) || has(ss.content) || has(ss.id)) return true;
    }
  }
  return false;
};

// ----- Reusable search box -----
const SearchBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: any;
}> = ({ value, onChange, placeholder, width }) => {
  const { t } = useTranslation();
  const border = useColorModeValue('gray.200', 'gray.600');
  const bg = useColorModeValue('white', 'gray.800');
  return (
    <Box position="relative" w={width || 'full'}>
      <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" pointerEvents="none" zIndex={1}>
        <Icon as={FiSearch} color="gray.400" boxSize={4} />
      </Box>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        pl={9}
        pr={value ? 9 : 3}
        bg={bg}
        borderColor={border}
        borderRadius="lg"
      />
      {value && (
        <IconButton
          aria-label={t('standards.clearSearch')}
          size="xs"
          variant="ghost"
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          onClick={() => onChange('')}
        >
          <FiX />
        </IconButton>
      )}
    </Box>
  );
};

// ----- Small stat card -----
const StatCard: React.FC<{ icon: any; label: string; value: number; color: string }> = ({
  icon,
  label,
  value,
  color,
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  return (
    <Card.Root bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" shadow="xs">
      <Card.Body p={4}>
        <HStack gap={3}>
          <Box p={2.5} borderRadius="lg" bg={`${color}.50`} color={`${color}.500`} _dark={{ bg: `${color}.900` }}>
            <Icon as={icon} boxSize={5} />
          </Box>
          <VStack align="start" gap={0}>
            <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
              {value}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {label}
            </Text>
          </VStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

const StandardsLibraryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: standards = [], isLoading } = useSystemStandards();

  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('standard'));
  const [query, setQuery] = useState('');
  const [classFilter, setClassFilter] = useState<string>('All');
  const [clauseQuery, setClauseQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedSubs, setExpandedSubs] = useState<Record<string, boolean>>({});

  // Keep the URL in sync so the standard can be deep-linked from the dashboard
  useEffect(() => {
    if (selectedId) setSearchParams({ standard: selectedId }, { replace: true });
    else setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Reset clause view when switching standards
  useEffect(() => {
    setClauseQuery('');
    setExpanded({});
    setExpandedSubs({});
  }, [selectedId]);

  const {
    data: sectionsResp,
    isLoading: sectionsLoading,
    isError: sectionsError,
  } = useQuery(
    ['standard-sections', selectedId],
    () => systemStandardsAPI.getSections(String(selectedId)),
    { enabled: !!selectedId, staleTime: 10 * 60 * 1000 }
  );

  const sections: any[] = sectionsResp?.sections || [];
  const selectedStandard = (standards as any[]).find((s) => String(s.id) === String(selectedId));

  // The distinct classifications actually present in the data (admin-set; no invented grouping)
  const classifications = useMemo(
    () =>
      Array.from(
        new Set((standards as any[]).map((s) => s.classification).filter(Boolean))
      ) as string[],
    [standards]
  );

  const allControlsCount = useMemo(
    () =>
      (standards as any[]).reduce(
        (sum, s) => sum + (s.total_sections ?? s.sections_data?.sections?.length ?? 0),
        0
      ),
    [standards]
  );

  const filteredStandards = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (standards as any[]).filter((s) => {
      if (classFilter !== 'All' && s.classification !== classFilter) return false;
      if (!q) return true;
      return (
        (s.name || '').toLowerCase().includes(q) ||
        (s.standard_type || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      );
    });
  }, [standards, classFilter, query]);

  const filteredSections = useMemo(() => {
    if (!clauseQuery.trim()) return sections;
    return sections.filter((sec) => sectionMatches(sec, clauseQuery.trim()));
  }, [sections, clauseQuery]);

  // Color tokens (computed once, never inside loops)
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'gray.700');
  const subBg = useColorModeValue('gray.50', 'gray.900');
  const detailHeaderBg = useColorModeValue('gray.50', 'gray.800');
  const titleColor = useColorModeValue('gray.800', 'gray.100');

  const expandAll = () => {
    const e: Record<string, boolean> = {};
    const es: Record<string, boolean> = {};
    sections.forEach((sec) => {
      e[sec.section_id] = true;
      (sec.subsections || []).forEach((sub: any) => {
        if (sub.sub_subsections?.length) es[sub.subsection_id] = true;
      });
    });
    setExpanded(e);
    setExpandedSubs(es);
  };
  const collapseAll = () => {
    setExpanded({});
    setExpandedSubs({});
  };

  const toggleSection = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const toggleSub = (id: string) => setExpandedSubs((p) => ({ ...p, [id]: !p[id] }));

  const searching = clauseQuery.trim().length > 0;

  const getClassificationLabel = (classification?: string) => {
    switch ((classification || '').toLowerCase()) {
      case 'standard':
        return t('standards.classificationStandard');
      case 'framework':
        return t('standards.classificationFramework');
      case 'regulation':
        return t('standards.classificationRegulation');
      default:
        return classification || '';
    }
  };

  const renderSubsection = (sub: any) => {
    const subId = sub.subsection_id;
    const subSubs: any[] = sub.sub_subsections || [];
    const hasChildren = subSubs.length > 0;
    const open = searching ? true : !!expandedSubs[subId];
    return (
      <Box key={subId} borderWidth="1px" borderColor={borderCol} borderRadius="md" p={3} bg={subBg}>
        <HStack
          justify="space-between"
          align="start"
          cursor={hasChildren ? 'pointer' : 'default'}
          onClick={() => hasChildren && toggleSub(subId)}
        >
          <VStack align="start" gap={1.5} flex={1} minW={0}>
            <HStack gap={2} align="start">
              <Badge colorPalette="gray" variant="subtle" fontSize="10px" flexShrink={0}>
                {sub.subsection_id}
              </Badge>
              <Text fontSize="xs" fontWeight="semibold" color={titleColor}>
                {sub.subsection_title}
              </Text>
            </HStack>
            {sub.subsection_content && (
              <Text fontSize="xs" color="gray.500" whiteSpace="pre-wrap">
                {stripIdPrefix(sub.subsection_content, sub.subsection_id)}
              </Text>
            )}
          </VStack>
          {hasChildren && (
            <Icon as={open ? FiChevronDown : FiChevronRight} color="gray.400" flexShrink={0} mt={1} />
          )}
        </HStack>
        {hasChildren && (
          <Collapsible.Root open={open}>
            <Collapsible.Content>
              <VStack align="stretch" gap={1.5} mt={2} pl={3} borderLeftWidth="2px" borderColor={borderCol}>
                {subSubs.map((ss: any) => (
                  <HStack key={ss.id} gap={2} align="start" pl={2}>
                    <Badge colorPalette="gray" variant="outline" fontSize="9px" flexShrink={0}>
                      {ss.id}
                    </Badge>
                    <Text fontSize="xs" color="gray.600">
                      {ss.content || ss.title}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Collapsible.Content>
          </Collapsible.Root>
        )}
      </Box>
    );
  };

  const renderSection = (section: any) => {
    const id = section.section_id;
    const subs: any[] = section.subsections || [];
    const open = searching ? true : !!expanded[id];
    const color = getStandardColor(selectedStandard?.standard_type || '');
    return (
      <Card.Root key={id} variant="outline" borderColor={borderCol} bg={cardBg} borderRadius="lg" overflow="hidden">
        <Box as="button" w="full" textAlign="left" p={4} _hover={{ bg: hoverBg }} onClick={() => toggleSection(id)}>
          <HStack justify="space-between" align="start">
            <HStack gap={3} align="start" flex={1} minW={0}>
              <Badge colorPalette={color} variant="solid" fontSize="11px" flexShrink={0}>
                {section.section_id}
              </Badge>
              <VStack align="start" gap={0.5} minW={0}>
                <Text fontWeight="semibold" fontSize="sm" color={titleColor}>
                  {section.section_title}
                </Text>
                {subs.length > 0 && (
                  <Text fontSize="11px" color="gray.400">
                    {t('standards.controlsCount', { count: subs.length })}
                  </Text>
                )}
              </VStack>
            </HStack>
            <Icon as={open ? FiChevronDown : FiChevronRight} color="gray.400" flexShrink={0} mt={1} />
          </HStack>
        </Box>
        <Collapsible.Root open={open}>
          <Collapsible.Content>
            <Box px={4} pb={4}>
              {section.section_content && (
                <Text fontSize="xs" color="gray.500" mb={3} whiteSpace="pre-wrap">
                  {section.section_content}
                </Text>
              )}
              {subs.length > 0 && (
                <VStack align="stretch" gap={2}>
                  {subs.map(renderSubsection)}
                </VStack>
              )}
            </Box>
          </Collapsible.Content>
        </Collapsible.Root>
      </Card.Root>
    );
  };

  const headerName = selectedStandard?.name || sectionsResp?.standard_name || t('standards.framework');
  const headerType = selectedStandard?.standard_type || '';
  const totalControls =
    sectionsResp?.total_sections ?? selectedStandard?.total_sections ?? sections.length ?? 0;

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 6, md: 8 }}>
      <Container maxW="7xl">
        <VStack align="stretch" gap={6}>
          {/* Page header */}
          <VStack align="start" gap={1}>
            <Heading size="lg" color={titleColor}>
              {t('standards.complianceFrameworks')}
            </Heading>
            <Text color="gray.500" fontSize="sm">
              {t('standards.complianceFrameworksDescription')}
            </Text>
          </VStack>

          {/* Stats (derived from the standards data) */}
          <SimpleGrid columns={{ base: 1, sm: 3 }} gap={3}>
            <StatCard icon={FiBookOpen} label={t('standards.frameworks')} value={(standards as any[]).length} color="blue" />
            <StatCard icon={FiList} label={t('standards.totalControls')} value={allControlsCount} color="purple" />
            <StatCard icon={FiLayers} label={t('standards.classifications')} value={classifications.length} color="teal" />
          </SimpleGrid>

          {/* Controls: search + classification filters (values come from the real data) */}
          <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" gap={3}>
            <SearchBox
              value={query}
              onChange={setQuery}
              placeholder={t('standards.searchStandards')}
              width={{ base: 'full', md: '340px' }}
            />
            {classifications.length > 1 && (
              <HStack gap={2} wrap="wrap">
                <Button
                  size="sm"
                  variant={classFilter === 'All' ? 'solid' : 'outline'}
                  colorPalette={classFilter === 'All' ? 'blue' : 'gray'}
                  borderRadius="full"
                  onClick={() => setClassFilter('All')}
                >
                  {t('standards.all')}
                </Button>
                {classifications.map((c) => (
                  <Button
                    key={c}
                    size="sm"
                    variant={classFilter === c ? 'solid' : 'outline'}
                    colorPalette={classFilter === c ? getClassificationColor(c) : 'gray'}
                    borderRadius="full"
                    onClick={() => setClassFilter(c)}
                  >
                    {c}
                  </Button>
                ))}
              </HStack>
            )}
          </Stack>

          {/* Master / detail */}
          <Grid templateColumns={{ base: '1fr', lg: '380px 1fr' }} gap={6} alignItems="start">
            {/* Left: standards list */}
            <GridItem display={{ base: selectedId ? 'none' : 'block', lg: 'block' }}>
              <Box
                maxH={{ lg: 'calc(100vh - 240px)' }}
                overflowY={{ lg: 'auto' }}
                pr={{ lg: 1 }}
                css={{
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'var(--chakra-colors-gray-300)',
                    borderRadius: '8px',
                  },
                }}
              >
                <VStack align="stretch" gap={2.5}>
                  {isLoading ? (
                    <HStack justify="center" py={10}>
                      <Spinner color="blue.500" />
                    </HStack>
                  ) : filteredStandards.length === 0 ? (
                    <Card.Root variant="outline" borderColor={borderCol}>
                      <Card.Body>
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          {t('standards.noFrameworksMatchSearch')}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  ) : (
                    filteredStandards.map((std) => {
                      const color = getStandardColor(std.standard_type);
                      const active = String(std.id) === String(selectedId);
                      const ctrlCount = std.total_sections ?? std.sections_data?.sections?.length ?? 0;
                      return (
                        <Card.Root
                          key={std.id}
                          variant="outline"
                          cursor="pointer"
                          bg={active ? activeBg : cardBg}
                          borderColor={active ? `${color}.400` : borderCol}
                          borderWidth={active ? '2px' : '1px'}
                          borderRadius="xl"
                          transition="all 0.15s"
                          _hover={{ borderColor: `${color}.300`, shadow: 'sm' }}
                          onClick={() => setSelectedId(String(std.id))}
                        >
                          <Card.Body p={4}>
                            <HStack gap={3} align="start">
                              <Box fontSize="2xl" lineHeight="1.1">
                                {getStandardIcon(std.standard_type)}
                              </Box>
                              <VStack align="start" gap={1.5} flex={1} minW={0}>
                                <Text fontWeight="bold" fontSize="sm" color={titleColor} lineClamp={1}>
                                  {std.name}
                                </Text>
                                <HStack gap={1.5} wrap="wrap">
                                  {std.classification && (
                                    <Badge
                                      colorPalette={getClassificationColor(std.classification)}
                                      variant="solid"
                                      fontSize="10px"
                                    >
                                      {getClassificationLabel(std.classification)}
                                    </Badge>
                                  )}
                                  <Badge colorPalette={color} variant="subtle" fontSize="10px">
                                    {std.standard_type}
                                  </Badge>
                                  {std.version && (
                                    <Text fontSize="10px" color="gray.400">
                                      v{std.version}
                                    </Text>
                                  )}
                                </HStack>
                                {std.description && (
                                  <Text fontSize="xs" color="gray.500" lineClamp={2}>
                                    {std.description}
                                  </Text>
                                )}
                                <HStack gap={1} color="gray.400">
                                  <Icon as={FiList} boxSize={3} />
                                  <Text fontSize="11px">{t('standards.controlsCount', { count: ctrlCount })}</Text>
                                </HStack>
                              </VStack>
                              <Icon
                                as={FiChevronRight}
                                color={active ? `${color}.500` : 'gray.300'}
                                flexShrink={0}
                              />
                            </HStack>
                          </Card.Body>
                        </Card.Root>
                      );
                    })
                  )}
                </VStack>
              </Box>
            </GridItem>

            {/* Right: clause detail */}
            <GridItem display={{ base: selectedId ? 'block' : 'none', lg: 'block' }}>
              {!selectedId ? (
                <Card.Root variant="outline" borderColor={borderCol} bg={cardBg} borderStyle="dashed">
                  <Card.Body>
                    <VStack gap={3} py={16}>
                      <Box p={4} borderRadius="full" bg="blue.50" color="blue.400" _dark={{ bg: 'blue.900' }}>
                        <Icon as={FiBookOpen} boxSize={8} />
                      </Box>
                      <Text fontWeight="semibold" color={titleColor}>
                        {t('standards.selectFrameworkToExploreClauses')}
                      </Text>
                      <Text fontSize="sm" color="gray.500" textAlign="center" maxW="sm">
                        {t('standards.pickFrameworkDescription')}
                      </Text>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ) : (
                <VStack align="stretch" gap={4}>
                  {/* Mobile back */}
                  <Button
                    display={{ base: 'inline-flex', lg: 'none' }}
                    variant="ghost"
                    size="sm"
                    alignSelf="start"
                    onClick={() => setSelectedId(null)}
                  >
                    <Icon as={FiArrowLeft} /> {t('standards.allFrameworks')}
                  </Button>

                  {/* Standard header */}
                  <Card.Root bg={detailHeaderBg} borderWidth="1px" borderColor={borderCol} borderRadius="xl">
                    <Card.Body p={5}>
                      <HStack gap={4} align="start">
                        <Box fontSize="3xl" lineHeight="1.1">
                          {getStandardIcon(headerType)}
                        </Box>
                        <VStack align="start" gap={2} flex={1} minW={0}>
                          <Heading size="md" color={titleColor}>
                            {headerName}
                          </Heading>
                          <HStack wrap="wrap" gap={2}>
                            {selectedStandard?.classification && (
                              <Badge colorPalette={getClassificationColor(selectedStandard.classification)} variant="solid">
                                {getClassificationLabel(selectedStandard.classification)}
                              </Badge>
                            )}
                            <Badge colorPalette={getStandardColor(headerType)} variant="subtle">
                              {headerType}
                            </Badge>
                            {selectedStandard?.version && (
                              <Badge colorPalette="gray" variant="subtle">
                                v{selectedStandard.version}
                              </Badge>
                            )}
                            <Badge colorPalette="gray" variant="subtle">
                              {t('standards.controlsCount', { count: totalControls })}
                            </Badge>
                          </HStack>
                          {selectedStandard?.description && (
                            <Text fontSize="sm" color="gray.600">
                              {selectedStandard.description}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Card.Body>
                  </Card.Root>

                  {/* Clause toolbar */}
                  <Stack direction={{ base: 'column', sm: 'row' }} justify="space-between" gap={2}>
                    <SearchBox
                      value={clauseQuery}
                      onChange={setClauseQuery}
                      placeholder={t('standards.searchClauses')}
                      width={{ base: 'full', sm: '320px' }}
                    />
                    <HStack gap={2}>
                      <Button size="xs" variant="outline" onClick={expandAll}>
                        {t('standards.expandAll')}
                      </Button>
                      <Button size="xs" variant="outline" onClick={collapseAll}>
                        {t('standards.collapseAll')}
                      </Button>
                    </HStack>
                  </Stack>

                  {/* Clauses */}
                  {sectionsLoading ? (
                    <HStack justify="center" py={16} gap={3}>
                      <Spinner color="blue.500" />
                      <Text fontSize="sm" color="gray.500">
                        {t('standards.loadingClauses')}
                      </Text>
                    </HStack>
                  ) : sectionsError ? (
                    <Card.Root variant="outline" borderColor={borderCol}>
                      <Card.Body>
                        <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                          {t('standards.couldNotLoadClauses')}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  ) : filteredSections.length === 0 ? (
                    <Card.Root variant="outline" borderColor={borderCol}>
                      <Card.Body>
                        <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                          {searching ? t('standards.noClausesMatchSearch') : t('standards.noClausesAvailable')}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  ) : (
                    <VStack align="stretch" gap={3}>
                      {filteredSections.map(renderSection)}
                    </VStack>
                  )}
                </VStack>
              )}
            </GridItem>
          </Grid>

          {/* Footer back link */}
          <Button variant="ghost" size="sm" alignSelf="start" onClick={() => navigate('/dashboard')}>
            <Icon as={FiArrowLeft} /> {t('standards.backToDashboard')}
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

export default StandardsLibraryPage;
